/**
 * Lead Status System Configuration
 * Includes all statuses, colors, icons, tooltips, and logic
 */

export const LEAD_STATUSES = {
  new: {
    value: 'new',
    label: 'New',
    color: 'bg-blue-100 text-blue-800',
    description: 'Auto-assigned when the Get Started form is submitted.',
    selectable: false, // Cannot be manually set
    autoAssigned: true
  },
  reviewing: {
    value: 'reviewing',
    label: 'Reviewing',
    color: 'bg-purple-100 text-purple-800',
    description: 'Auto-assigned the first time a staff/admin opens the lead (if status = New).',
    selectable: false, // Cannot be manually set
    autoAssigned: true
  },
  prequalified: {
    value: 'prequalified',
    label: 'Prequalified',
    color: 'bg-indigo-100 text-indigo-800',
    description: 'This lead looks like a potential fit and is worth pursuing.',
    selectable: true,
    requiresReason: false
  },
  call_scheduled: {
    value: 'call_scheduled',
    label: 'Call Scheduled',
    color: 'bg-cyan-100 text-cyan-800',
    description: 'Strategy call has been scheduled with the client.',
    selectable: false, // Set via call details form
    autoAssigned: true,
    showCallDetails: true
  },
  call_completed_deciding: {
    value: 'call_completed_deciding',
    label: 'Call Completed – Deciding',
    color: 'bg-yellow-100 text-yellow-800',
    description: 'Call happened but client hasn\'t committed yes/no yet.',
    selectable: true,
    requiresReason: true,
    reasons: [
      'Needs to talk to partner',
      'Wants more info',
      'Waiting on budget approval',
      'Needs time to think',
      'Other'
    ]
  },
  won_to_convert: {
    value: 'won_to_convert',
    label: 'Won – To Convert',
    color: 'bg-green-100 text-green-800',
    description: 'Client verbally said yes but hasn\'t signed the contract yet.',
    selectable: true,
    requiresReason: false
  },
  converted_to_client: {
    value: 'converted_to_client',
    label: 'Converted to Client',
    color: 'bg-emerald-100 text-emerald-800',
    description: 'Contract signed! Lead is now a paying client.',
    selectable: false, // Only accessible via Convert to Client button
    requiresReason: false,
    autoAssigned: true
  },
  lost: {
    value: 'lost',
    label: 'Lost',
    color: 'bg-red-100 text-red-800',
    description: 'Lead decided not to move forward.',
    selectable: true,
    requiresReason: true,
    reasons: [
      'Not a fit',
      'No budget',
      'Ghosted',
      'Chose another agency',
      'Timing not right',
      'Didn\'t want ads',
      'Other'
    ]
  },
  archived: {
    value: 'archived',
    label: 'Archived',
    color: 'bg-gray-100 text-gray-800',
    description: 'Lead is archived and hidden from active views.',
    selectable: true, // Can be manually set
    requiresReason: false
  }
};

// Get selectable statuses for dropdowns
export const getSelectableStatuses = () => {
  return Object.values(LEAD_STATUSES).filter(status => status.selectable);
};

// Get status config by value
export const getStatusConfig = (statusValue) => {
  return LEAD_STATUSES[statusValue] || LEAD_STATUSES.new;
};

// Get color class for status
export const getStatusColor = (statusValue) => {
  const config = getStatusConfig(statusValue);
  return config.color;
};

// Check if status requires a reason
export const statusRequiresReason = (statusValue) => {
  const config = getStatusConfig(statusValue);
  return config.requiresReason || false;
};

// Get reasons for a status
export const getStatusReasons = (statusValue) => {
  const config = getStatusConfig(statusValue);
  return config.reasons || [];
};
